<script setup>
import {computed} from "vue";
import * as yup from "yup";
import {useMutation, useQuery} from "@vue/apollo-composable";
import {requiredMessage, stringValidator} from "@/utils/yupValidators";
import {useDjangoForm} from "@/utils/forms";
import {useLastResult} from "@/graphql/composable";
import {SaveInfolineCallerMutation} from "@/graphql/mutations/hpo/infoline/caller.graphql";
import {InfolineCallerSearchQuery} from "@/graphql/queries/hpo/infoline/caller.graphql";
import CallerListItem from "./CallerListItem.vue";

const props = defineProps({
  hpo: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectCaller']);

const {defineField, handleMutationSubmit, resetForm} = useDjangoForm({
  validationSchema: yup.object({
    firstName: stringValidator.required(requiredMessage),
    lastName: stringValidator.required(requiredMessage),
    phoneNumber: stringValidator.required(requiredMessage),
  }),
  // initialValues: {
  //   lastName: "Novák",
  //   firstName: "Jan",
  //   phoneNumber: "+420777245897",
  // }
});

const [firstName, firstNameProps] = defineField('firstName');
const [lastName, lastNameProps] = defineField('lastName');
const [phoneNumber, phoneNumberProps] = defineField('phoneNumber');

// Live OR search of existing callers, reactive to the three fields, debounced.
const anyFieldFilled = computed(() =>
  !!firstName.value?.trim() || !!lastName.value?.trim() || !!phoneNumber.value?.trim()
);

const {result: searchResult} = useQuery(
  InfolineCallerSearchQuery,
  () => ({
    hpoId: props.hpo.id,
    firstName: firstName.value,
    lastName: lastName.value,
    phoneNumber: phoneNumber.value,
  }),
  // Read props.active inside the getter — do NOT destructure (keeps reactivity).
  () => ({
    enabled: props.active && anyFieldFilled.value,
    debounce: 300,
  }),
);
const matches = useLastResult(searchResult, [], data => data.infolineCallerSearch);

const {mutate} = useMutation(SaveInfolineCallerMutation);

const onSubmit = handleMutationSubmit(
  (values) => mutate({...values, hpo: props.hpo.id}),
  {
    // Calls handleSelectCaller (parent CallModal currently calls an undefined
    // `selectCaller` here — this is the repaired create-success path).
    onSuccess(response) {
      handleSelectCaller(response.infolineCaller);
    },
  },
);

// Used by both a match click and a successful create: re-populate the form with
// the caller, then notify the parent so it can record the selection and advance
// to the (stubbed) Patients tab.
function handleSelectCaller(caller) {
  resetForm({
    values: {
      firstName: caller.firstName,
      lastName: caller.lastName,
      phoneNumber: caller.phoneNumber,
    },
  });
  emit('selectCaller', caller);
}
</script>

<template>
  <v-row>
    <v-col cols="12">
      <v-form @submit.prevent="onSubmit">
        <v-card :title="pgettext('label', 'Caller Identification')">
          <v-card-text>
            <v-text-field v-model="lastName"
                          v-bind="lastNameProps"
                          :label="pgettext('label','Last Name')"/>
            
            <v-text-field v-model="firstName"
                          v-bind="firstNameProps"
                          :label="pgettext('label','First Name')"/>

            <v-text-field v-model="phoneNumber"
                          v-bind="phoneNumberProps"
                          type="tel"
                          autocomplete="off"
                          :label="pgettext('label','Phone number')"/>
          </v-card-text>
          <v-card-actions>
                <v-btn
                  block
                  variant="elevated"
                  type="submit"
                  size="x-large"
                  :text="pgettext('action', 'Continue')"
            />
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
  </v-row>
</template>

<style scoped>
</style>
